import type { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';

import { useLocaleWeekAndMonth, L10nDate } from '@travel/i18n';
import LocaleDatePicker from '@travel/traveler-core/components/LocaleDatePicker';
import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import {
  FULL_DATE_WITH_DASHES,
  MONTH_DAY_YOUBI_WITH_SPACE_FORMAT,
  YEAR_MONTH_KANJI_DATE,
} from '@travel/traveler-core/utils/dateTime';
import { timezoneDayjs as dayjs } from '@travel/traveler-core/utils/jrdpHelper';
import type { SelectedState } from '@travel/ui/components/CalendarBody/CalendarDay';
import DatePickerBody from '@travel/ui/components/DatePicker/DatePickerBody';
import { cx } from '@travel/utils';

import labels from 'favoriteList/components/SearchForm/constants/labels';
import { getEarliestDate, MAX_STAY_NIGHT } from 'favoriteList/components/SearchForm/utils/search';

import styles from './datePicker.module.scss';

type SelectedDateRange = { start?: string; end?: string };

type Props = {
  className?: string;
  /** String of current selected start date */
  startDate?: string;
  /** String of current selected end date */
  endDate?: string;
  /** Start date label */
  startDateLabel?: React.ReactNode;
  /** End date label*/
  endDateLabel?: React.ReactNode;
  /** Callback to be called when selected date changed */
  onDateChanged: (startDate?: string, endDate?: string, isCalendarOpen?: boolean) => void;
  /** Flag to define whether the calendar should be open or not */
  isCalendarOpen: boolean;
  /** Callback to be called to open calendar */
  onOpenCalendar: () => void;
  /** Callback to be called to close calendar */
  onCloseCalendar: () => void;
  /** Extra component to display on calendar per day */
  PerDayComponent?: React.ComponentType<{ day: Dayjs; selectedState: SelectedState }>;
  /** Number of expected popup height in pixel */
  customCalendarHeight?: number;
  /** Node to be anchor element for popup */
  anchorEl: HTMLDivElement | null;
  /** For partial checkout, keep a reference end date static to keep available selection range open */
  fixedReferenceEndDate?: string;
  /** Callback to be called when active button state has changed */
  onActiveButtonChanged?: (nextActiveButton: string) => void;
  /** String to define what is the current selected button */
  selectedButton?: 'startDate' | 'endDate';
  /** List of holidays */
  holidays?: string[];
};

function DatePicker(props: Props) {
  const {
    className,
    startDate,
    endDate,
    onDateChanged,
    isCalendarOpen,
    onCloseCalendar,
    PerDayComponent,
    customCalendarHeight,
    anchorEl,
    onActiveButtonChanged,
    selectedButton: expectedSelectedButton,
    startDateLabel = labels.checkIn,
    endDateLabel = labels.checkOut,
    holidays,
  } = props;

  const { weekdaysShort, monthsShort, firstDayOfWeek } = useLocaleWeekAndMonth();

  const isSP = useDeviceType() === MOBILE_SCREEN;
  const defaultSelectedButton = expectedSelectedButton || 'startDate';

  const [selectedButton, setSelectedButton] = useState<'startDate' | 'endDate'>(
    defaultSelectedButton,
  );
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: startDate,
    end: endDate,
  } as SelectedDateRange);
  const isSelectedCheckInButton = selectedButton === 'startDate';
  const isDatePropUpdated = useRef(false);
  const maxCheckInDate = useMemo(() => {
    const today = dayjs();
    return today.add(2, 'year').month(11).date(31).toString();
  }, []);
  const referenceEndDate = useMemo(() => {
    const maxCheckoutDate = dayjs(startDate).add(MAX_STAY_NIGHT, 'day');

    return startDate && selectedButton === 'endDate' && maxCheckoutDate.isBefore(maxCheckInDate)
      ? maxCheckoutDate.toString()
      : maxCheckInDate;
  }, [startDate, selectedButton, maxCheckInDate]);

  const onSelectButton = useCallback((_event: any, button: string) => {
    setSelectedButton(button as 'startDate' | 'endDate');
  }, []);

  const onClear = () => {
    setSelectedDateRange({ start: undefined, end: undefined });
    onCloseCalendar();
  };

  /** effect when given prop button state is changed */
  useEffect(() => {
    if (expectedSelectedButton) onSelectButton?.(null, expectedSelectedButton);
  }, [expectedSelectedButton, onSelectButton]);

  /** effect when internal button state's active is changed */
  useEffect(() => {
    onActiveButtonChanged?.(selectedButton);
  }, [selectedButton, onActiveButtonChanged]);

  /**
   * (1) effect to update internal state when prop is updated
   * ref ie being used to prevent recursive updated between internal state and new given props
   */
  useEffect(() => {
    setSelectedDateRange({
      start: startDate,
      end: endDate,
    });
    isDatePropUpdated.current = true;
  }, [startDate, endDate]);

  /**
   * (2) effect to update parent state when internal state is updated
   * ref is being cleared to allow component to trigger date changed next time
   *
   * this effect has to be after (1) effect
   */
  useEffect(() => {
    if (!isDatePropUpdated.current) {
      onDateChanged(selectedDateRange.start, selectedDateRange.end, isCalendarOpen);
    } else {
      isDatePropUpdated.current = false;
    }
  }, [selectedDateRange, onDateChanged, isCalendarOpen]);

  const earliestPossibleDate = getEarliestDate() as Dayjs;

  const referenceStartDate = earliestPossibleDate.format(FULL_DATE_WITH_DASHES);

  const onSelectDate = useCallback(
    (_event: React.MouseEvent | React.KeyboardEvent, day: Dayjs) => {
      setSelectedDateRange((prevState) => {
        const { start, end } = prevState;

        const isSelectedEndDayBeforeOrOnExistingStartDay = day.isSameOrBefore(dayjs(start));
        const formattedDate = day.format(FULL_DATE_WITH_DASHES);

        if (isSelectedCheckInButton) {
          // If the current button is check in, clear the check out date regardless
          setSelectedButton('endDate');
          if (start) {
            return {
              start: formattedDate,
              end: undefined,
            };
          } else {
            return {
              start: formattedDate,
              end: end,
            };
          }
        }
        // if the check-out button is active
        // if the selected day is before start day
        // then set start day as selected day, and set start day as end day
        if (isSelectedEndDayBeforeOrOnExistingStartDay) return { start: formattedDate, end: start };
        // ; when the selected start date is not define, then activate the check-in button
        if (!start) setSelectedButton('startDate');
        // ; when the given end date is after the selected start date, then use it as selected end date
        return { start, end: formattedDate };
      });
    },
    [isSelectedCheckInButton],
  );

  const onCloseCalendarPc = useCallback(() => {
    const { start } = selectedDateRange;
    if (dayjs(start).isSame(maxCheckInDate, 'day')) {
      setSelectedDateRange({
        start: dayjs(start).subtract(1, 'day').format(FULL_DATE_WITH_DASHES),
        end: start,
      });
    }
    onCloseCalendar();
  }, [onCloseCalendar, selectedDateRange, maxCheckInDate]);

  useEffect(() => {
    if (selectedDateRange.start && selectedDateRange.end && !isSP && isCalendarOpen) {
      setTimeout(() => onCloseCalendarPc(), 600);
    }
  }, [selectedDateRange, isSP, onCloseCalendarPc]);

  const showingWeekdayComponent = ({ value }) => (
    <span className={styles.dateButtonText}>
      <L10nDate value={value} format={MONTH_DAY_YOUBI_WITH_SPACE_FORMAT} />
    </span>
  );

  const commonProps = {
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
    referenceDate: referenceStartDate,
    referenceEndDate: referenceEndDate,
    weekType: 'week' as const,
    dayWrapperClassName: cx(styles.dayWrapper, styles.coloredCalendarDays),
    PerDayComponent: PerDayComponent,
    customCalendarHeight: customCalendarHeight,
    labelDateFormat: YEAR_MONTH_KANJI_DATE,
    calendarLabelClassName: styles.monthLabels,
    calendarWeekHeaderClassName: cx(
      styles.calendarWeekHeader,
      styles.coloredWeekendDays,
      styles.customHeaderSpacing,
    ),
    calendarWrapperClassName: styles.calendarWrapper,
    className: className,
    navigationIconSize: 16,
    navigationIconClassName: styles.navigationIcon,
    dayItemClassName: styles.dayItem,
    calendarBodyClass: styles.calendarBody,
    shouldAllowInvalidMonthDays: true,
    holidays: holidays,
    shouldGetAllDayStates: true,
    shouldHideDisabledMonthNavigation: true,
    shouldShowEntireInvalidMonth: false,
  };

  return !isSP ? (
    <LocaleDatePicker
      {...commonProps}
      className={className}
      popupProps={{ anchorEl: anchorEl }}
      numberOfMonths={2}
      type={'popup'}
      isOpen={isCalendarOpen}
      onClose={!isSP ? onCloseCalendarPc : onCloseCalendar}
      onSelectDate={onSelectDate}
      selectedButton={selectedButton}
      startDateLabel={startDateLabel}
      endDateLabel={endDateLabel}
      customPopupInfoBoxComponent={<Fragment />}
      dialogActionClassName={styles.buttonPadding}
      DateComponent={showingWeekdayComponent}
      onSelectButton={onSelectButton}
      onClear={onClear}
      shouldEnableInvalidRange={true}
    />
  ) : (
    <DatePickerBody
      {...commonProps}
      isShowNavigation={true}
      className={styles.datePickerBody}
      type="popup"
      numberOfMonths={1}
      onClickDayItem={onSelectDate}
      weekdayLabels={weekdaysShort}
      monthLabels={monthsShort}
      weekType={firstDayOfWeek ? 'isoWeek' : 'week'}
    />
  );
}
export default DatePicker;
